import { stopsAreSameStop, TrainIdentifier } from "@vygruppen/vy-train-map";
import {
  ALTERNATIVE_STRETCHES,
  Stretch,
} from "features/CenterContent/shared/operationalInformation/alternativeStretches";
import {
  mapStopToStopIdSchema,
  StopId,
} from "features/CenterContent/shared/operationalInformation/utils";
import { trainRouteStopToStopId } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/AffectedStopsFields";
import { TrainRouteChangedEventsResponse } from "features/CenterContent/VehicleDetails/TrainDetails/useRouteChangedEvents";
import { TrainRoute } from "features/CenterContent/VehicleDetails/TrainDetails/useTrainRoute";
import { getStopContext } from "shared/types/countryCode";

export const findAlternativeStretch = (
  stretch: Stretch,
): Stretch | undefined => {
  if (stretch === undefined || stretch.name === "-") {
    return undefined;
  }
  return (
    ALTERNATIVE_STRETCHES.find(
      (s) =>
        (s.from === stretch.from &&
          s.to === stretch.to &&
          s.name !== stretch.name) ||
        (s.from === stretch.to &&
          s.to === stretch.from &&
          s.name !== stretch.name),
    ) ?? ALTERNATIVE_STRETCHES[0]
  );
};

export const isRelevantStretch = (
  stretch: Stretch,
  route: TrainRoute | undefined,
) => {
  if (route === undefined) return false;
  if (stretch.name === "-") return true;
  const routeStops = route.stops.map((stop) => stop.stopId);
  const stretchStops = stretch.stops.flatMap(({ stopIds }) =>
    stopIds.map(({ id }) => id),
  );
  return (
    routeStops.includes(stretchStops[0]) &&
    routeStops.includes(stretchStops[stretchStops.length - 1])
  );
};

export const eventAlreadyExists = (
  stretch: Stretch,
  existingEvents: TrainRouteChangedEventsResponse[],
  uuid?: string,
) => {
  if (stretch.stops.length < 1) return false;

  const firstStopCodes = stretch.stops[0].stopIds.map(({ id }) => id);
  const lastStopCodes = stretch.stops[stretch.stops.length - 1].stopIds.map(
    ({ id }) => id,
  );
  return existingEvents.some((event) => {
    if (event.uuid === uuid) {
      return false;
    }
    if (
      firstStopCodes.some((code) =>
        event.firstStop.ids.some(({ id }) => id === code),
      ) &&
      lastStopCodes.some((code) =>
        event.lastStop.ids.some(({ id }) => id === code),
      )
    ) {
      return true;
    }
    if (
      firstStopCodes.some((code) =>
        event.lastStop.ids.some(({ id }) => id === code),
      ) &&
      lastStopCodes.some((code) =>
        event.firstStop.ids.some(({ id }) => id === code),
      )
    ) {
      return true;
    }
    return false;
  });
};

export const shouldFlipStretchDirection = (
  stretch: Stretch,
  route: TrainRoute | undefined,
): boolean => {
  if (route === undefined) return false;
  const fromStop = stretch?.stops[0];
  const toStop = stretch?.stops[stretch.stops.length - 1];
  const stops = route?.stops.map((stop) => stop.stopId) ?? [];
  const fromIndex = stops.findIndex((code) =>
    fromStop.stopIds.some(({ id }) => code === id),
  );
  const toIndex = stops.findIndex((code) =>
    toStop.stopIds.some(({ id }) => code === id),
  );
  if (fromIndex === -1 && toIndex === -1) return false;
  if (fromIndex === -1 || toIndex === -1) return fromIndex < toIndex;
  return toIndex < fromIndex;
};

export const flip = <T>(stops: T[], shouldFlip: boolean = true) => {
  if (shouldFlip) {
    return stops.toReversed();
  }
  return stops;
};

export const formatStretchName = (stretch: Stretch, flipDirection: boolean) => {
  if (stretch.name === "-") {
    return "-";
  }
  return `${stretch.name} (${flipDirection ? stretch.to : stretch.from} - ${
    flipDirection ? stretch.from : stretch.to
  })`;
};

const affectedStopsMatchStopsInStretch = (
  affectedStops: StopId[],
  stops: StopId[],
) => {
  if (affectedStops.length !== stops.length) return false;
  const lastIndex = affectedStops.length - 1;
  const firstAffectedStopId = affectedStops[0].id;
  const lastAffectedStopId = affectedStops[lastIndex].id;
  const firstStopId = stops[0].id;
  const lastStopId = stops[lastIndex].id;
  const matchesSameDirection =
    firstAffectedStopId === firstStopId && lastAffectedStopId === lastStopId;
  const matchesOtherDirection =
    firstAffectedStopId === lastStopId && lastAffectedStopId === firstStopId;
  return matchesSameDirection || matchesOtherDirection;
};

/**
 * We can't find a stretch using only `name`, as it is not unique.
 * Therefore, we find a stretch using `affectedStops`, as it is unique (tested in `alternativeStretches.test.tsx`)
 */
export const findReplacedStretchIndexByAffectedStops = (
  affectedStops: StopId[],
) => {
  if (!affectedStops || affectedStops.length === 0) return -1;
  return ALTERNATIVE_STRETCHES.findIndex((stretch: Stretch) =>
    affectedStopsMatchStopsInStretch(
      affectedStops,
      stretch.stops.map(mapStopToStopIdSchema),
    ),
  );
};

export const findNewStretchIndexByAffectedStops = (affectedStops: StopId[]) => {
  const replacedStretchIndex =
    findReplacedStretchIndexByAffectedStops(affectedStops);
  const replacedStretch = ALTERNATIVE_STRETCHES[replacedStretchIndex];
  const newStretch = findAlternativeStretch(replacedStretch);
  if (!newStretch) return -1;
  return ALTERNATIVE_STRETCHES.indexOf(newStretch);
};

export const makeStretchStops = (
  newStretch: Stretch,
  replacedStretch: Stretch,
  trainRoute: TrainRoute | undefined,
  trainId: TrainIdentifier,
) => {
  // Affected stops are unidirectional but the trains may be running the other
  // direction. If so, we flip the affected stops array to match the route.
  const shouldFlip = shouldFlipStretchDirection(replacedStretch, trainRoute);

  // Create a map of stopId to name from trainRoute for stop name lookups
  const trainRouteStops = trainRoute?.stops ?? [];

  // Stops that are in the old route but not the new one
  const cancelledStopsFiltered = replacedStretch.stops.filter(
    (stop) =>
      !newStretch.stops.some((newStop) => stopsAreSameStop(stop, newStop)),
  );
  const cancelledStopsFlipped = flip(cancelledStopsFiltered, shouldFlip);
  const cancelledStops = cancelledStopsFlipped.map((stop) => {
    const mapped = mapStopToStopIdSchema(stop);
    return { ...mapped, context: getStopContext(trainId.countryCode) };
  });

  const affectedStopsFlipped = flip(replacedStretch.stops, shouldFlip);
  const affectedStops = affectedStopsFlipped.map((stop) => {
    const mapped = mapStopToStopIdSchema(stop);
    return { ...mapped, context: getStopContext(trainId.countryCode) };
  });

  // The full route of the train, with the affected stretch replaced by
  // the new one
  const firstAffectedStopIndex = trainRouteStops.findIndex(
    (stop) => stop.stopId === affectedStops[0]?.id,
  );
  const lastAffectedStopIndex = trainRouteStops.findIndex(
    (stop) => stop.stopId === affectedStops.at(-1)?.id,
  );
  const numberOfStopsToReplace =
    lastAffectedStopIndex - firstAffectedStopIndex + 1; // Inclusive of last stop

  const newStretchStopsFlipped = flip(newStretch.stops, shouldFlip);

  // Convert new stretch stops to stopIdSchema format
  const newStretchStopSchemas = newStretchStopsFlipped.map(
    mapStopToStopIdSchema,
  );

  // Convert train route stops to stopIdSchema format
  const stopIds: StopId[] = trainRouteStops.map((stop) =>
    trainRouteStopToStopId(stop, trainId.countryCode),
  );

  // Splice the new stretch into the route
  const newRoute = stopIds.toSpliced(
    firstAffectedStopIndex,
    numberOfStopsToReplace,
    ...newStretchStopSchemas,
  );

  return {
    cancelledStops,
    affectedStops,
    newRoute,
  };
};
